import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, UncontrolledTooltip } from 'reactstrap';
import LabelGroup from 'components/Label-Group';
import TextInput from 'components/Text-Input';
import TextAreaInput from 'components/TextArea-Input';
import { ItemStyles } from '../Tab-Pane-Media/styles';
import { useKanban } from 'hooks/useKanban';

const Phase = ({
  id,
  name: initialName,
  description: initialDescription,
  setColumns,
  index,
  total,
  isNew,
  setSelectedPhase,
  setIsOpenModalDelete,
  setStatus,
}) => {
  const { t: translate } = useTranslation();
  const { kanban: paidMedia } = useKanban();
  const [editMode, setEditMode] = useState(!initialName);
  const [errors, setErrors] = useState({});
  const [name, setName] = useState(initialName ?? '');
  const [description, setDescription] = useState(initialDescription ?? '');

  const moveUp = async () => {
    if (index === 0) return;

    setColumns((prev) => {
      const newCols = [...prev];
      newCols[index - 1].position = index;
      newCols[index].position = index - 1;
      return newCols;
    });

    try {
      await paidMedia.moveColumnBackward(id);
    } catch (error) {
      console.error('Failed to move column up:', error);
    }
  };

  const moveDown = async () => {
    if (index === total - 1) return;

    setColumns((prev) => {
      const newCols = [...prev];
      newCols[index + 1].position = index;
      newCols[index].position = index + 1;
      return newCols;
    });

    try {
      await paidMedia.moveColumnForward(id);
    } catch (error) {
      console.error('Failed to move column down:', error);
    }
  };

  const handleCancel = () => {
    if (isNew) {
      setColumns((prev) => prev.filter((col) => col.id !== id));
      setEditMode(false);
      return;
    }

    setEditMode(false);
    setName(initialName);
    setDescription(initialDescription);
    setErrors({});
  };

  const handleDelete = async () => {
    try {
      setSelectedPhase({ id, name, description, isNew: isNew || false });
      setIsOpenModalDelete(true);
    } catch (error) {
      console.error('Failed to delete column:', error);
      setSelectedPhase({});
    }
  };

  const handleSave = async () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.title = { message: 'Required Field!' };
    } else if (!/[A-Za-z_]/.test(name)) {
      newErrors.title = { message: 'Title must contain at least a letter!' };
    }

    if (!description.trim()) {
      newErrors.description = { message: 'Required Field!' };
    } else if (!/[A-Za-z_]/.test(description)) {
      newErrors.description = { message: 'Description must contain at least a letter!' };
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setEditMode(true);
      return;
    }

    try {
      let savedColumn = { id, name, description };
      if (isNew) {
        savedColumn = await paidMedia.createColumn({ title: name, description });
        setStatus('added');
      } else {
        savedColumn = await paidMedia.updateColumn(id, { title: name, description });
        setStatus('saved');
      }

      setColumns((prev) => prev.map((col) => (col?.id === id ? savedColumn : col)));

      setEditMode(false);
    } catch (error) {
      console.error('Failed to save changes:', error);
    }
  };

  return (
    <ItemStyles className={editMode || !name ? 'align-items-start' : 'align-items-center'}>
      <div className="d-flex gap-1">
        <Button color="outline" onClick={moveUp} disabled={index === 0}>
          <i className="bx bx-chevron-up" style={{ color: '#343A40' }} />
        </Button>
        <Button color="outline" onClick={moveDown} disabled={index === total - 1}>
          <i className="bx bx-chevron-down" style={{ color: '#343A40' }} />
        </Button>
      </div>
      {editMode ? (
        <div className="w-100">
          <LabelGroup htmlFor="title" label="Title" mb={2} column required size={12}>
            <TextInput
              controlName="title"
              placeholder="Title"
              value={name}
              validation={{
                pattern: {
                  value: /[A-Za-z_]/,
                  message: 'Title must contain at least a letter!',
                },
              }}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.title) {
                  setErrors((prev) => {
                    const { title, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  handleCancel();
                }
              }}
              style={{
                borderColor: errors.title ? 'red' : undefined,
              }}
              maxLength={80}
              noEllipsis
              required
            />
            {errors.title && (
              <span style={{ color: 'red' }}>{translate(errors.title.message)}</span>
            )}
          </LabelGroup>
          <LabelGroup htmlFor="description" label="Description" column required size={12}>
            <TextAreaInput
              controlName="description"
              placeholder="Description"
              value={description}
              validation={{
                pattern: {
                  value: /[A-Za-z_]/,
                  message: 'Description must contain at least a letter!',
                },
              }}
              onChange={(e) => {
                setDescription(e.target.value);
                if (errors.description) {
                  setErrors((prev) => {
                    const { description, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  handleCancel();
                }
              }}
              style={{
                borderColor: errors.description ? 'red' : undefined,
              }}
              noEllipsis
              required
            />
            {errors.description && (
              <span style={{ color: 'red' }}>{translate(errors.description.message)}</span>
            )}
          </LabelGroup>
          <div className="d-flex w-100 justify-content-between">
            {!isNew ? (
              <Button
                type="button"
                color="ghost"
                className="btn btn-delete w-auto"
                onClick={handleDelete}
              >
                {translate('Exclude')}
              </Button>
            ) : (
              <div></div>
            )}
            <div className="d-flex gap-2">
              <Button
                type="button"
                color="outline"
                className="btn btn-cancel w-auto"
                onClick={handleCancel}
              >
                {translate('Cancel')}
              </Button>
              <Button
                type="submit"
                color="primary"
                className="btn btn-save w-auto"
                onClick={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
              >
                {isNew ? translate('Add phase') : translate('Save change')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="d-flex justify-content-between w-100 align-items-center">
          <span className="w-100">{name}</span>
          <div className="d-flex gap-1 align-self-end">
            <Button
              color="ghost"
              id={`${id}-btn-edit`}
              className="btn-small"
              onClick={() => setEditMode(!editMode)}
            >
              <i className="bx bx-edit" style={{ fontSize: '1.25em', color: '#74788D' }} />
            </Button>
            <Button
              color="ghost"
              id={`${id}-btn-delete`}
              className="btn-small"
              onClick={handleDelete}
            >
              <i
                className="bx bx-trash"
                style={{ fontSize: '1.25em', color: 'rgba(244, 106, 106, 1)' }}
              />
            </Button>
            <UncontrolledTooltip target={`${id}-btn-edit`} placement="top">
              {translate('Edit')}
            </UncontrolledTooltip>
            <UncontrolledTooltip target={`${id}-btn-delete`} placement="top">
              {translate('Exclude')}
            </UncontrolledTooltip>
          </div>
        </div>
      )}
    </ItemStyles>
  );
};

Phase.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  setColumns: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  accountParam: PropTypes.string.isRequired,
  isNew: PropTypes.bool,
  setSelectedPhase: PropTypes.func.isRequired,
  setIsOpenModalDelete: PropTypes.func.isRequired,
  setStatus: PropTypes.func.isRequired,
};

export default Phase;
